import { zodResolver } from '@hookform/resolvers/zod';
import EmailRounded from '@mui/icons-material/EmailRounded';
import ForwardToInboxRounded from '@mui/icons-material/ForwardToInboxRounded';
import MarkEmailReadRounded from '@mui/icons-material/MarkEmailReadRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import SmsRounded from '@mui/icons-material/SmsRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { DataTable, type Column } from '../../../components/DataTable';
import { EmptyState } from '../../../components/states';
import { StatusChip } from '../../../components/StatusChip';
import { dateTime, relativeTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { keys, type Invitation, type InvitationSend } from '../api';
import { applyFieldErrors } from '../lib';
import { CopyField, FieldGrid } from '../ui';
import { RolePicker } from './RolePicker';
import { PersonAvatar } from './TeamTab';

type Channel = Invitation['channel'];
const CHANNELS: { value: Channel; label: string; icon: typeof EmailRounded }[] = [
  { value: 'EMAIL', label: 'Email', icon: EmailRounded },
  { value: 'SMS', label: 'SMS', icon: SmsRounded },
  { value: 'WHATSAPP', label: 'WhatsApp', icon: WhatsApp },
];

type Filter = 'PENDING' | 'ALL' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';

export function InvitationsTab({
  onInvite,
  onSent,
}: {
  onInvite: () => void;
  onSent: (result: InvitationSend) => void;
}) {
  const { can } = useAuth();
  const { format } = useShop();
  const canManage = can('staff.manage');
  const qc = useQueryClient();
  const notify = useNotify();
  const [filter, setFilter] = useState<Filter>('PENDING');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const query = { page, pageSize, ...(filter !== 'ALL' ? { status: filter } : {}) };
  const { data, isLoading } = useQuery({
    queryKey: [...keys.invitations, query],
    queryFn: () => unwrap(api.GET('/staff/invitations', { params: { query } })),
    placeholderData: keepPreviousData,
  });

  const resend = async (inv: Invitation) => {
    setBusy(inv.id);
    try {
      const r = await unwrap(
        api.POST('/staff/invitations/{id}/resend', { params: { path: { id: inv.id } } }),
      );
      await qc.invalidateQueries({ queryKey: keys.invitations });
      void qc.invalidateQueries({ queryKey: keys.subscriptionStatus });
      onSent(r);
    } catch (e) {
      notify.error(e);
    } finally {
      setBusy(null);
    }
  };

  const columns: Column<Invitation>[] = [
    {
      key: 'name',
      header: 'Invited',
      render: (i) => {
        const ch = CHANNELS.find((c) => c.value === i.channel);
        const Icon = ch?.icon ?? EmailRounded;
        return (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
            <PersonAvatar name={i.name} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
                {i.name}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Icon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {i.email ?? i.phone ?? '—'}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        );
      },
    },
    {
      key: 'roles',
      header: 'Roles',
      hideBelow: 'md',
      render: (i) => (
        <Typography variant="body2" noWrap>
          {i.roles.map((r) => r.name).join(', ')}
        </Typography>
      ),
    },
    { key: 'status', header: 'Status', render: (i) => <StatusChip status={i.status} /> },
    {
      key: 'when',
      header: 'Expires / sent',
      hideBelow: 'sm',
      render: (i) => (
        <Tooltip title={`Expires ${dateTime(i.expiresAt, format)}`}>
          <Box>
            <Typography variant="body2">
              {i.status === 'ACCEPTED' && i.acceptedAt
                ? `Joined ${relativeTime(i.acceptedAt)}`
                : i.status === 'PENDING'
                  ? `Expires ${relativeTime(i.expiresAt)}`
                  : `Sent ${relativeTime(i.lastSentAt)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sent {i.sendCount === 1 ? 'once' : `${String(i.sendCount)} times`}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (i) =>
        canManage &&
        (i.status === 'PENDING' || i.status === 'EXPIRED') && (
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title="Send again with a new link">
              <IconButton
                size="small"
                aria-label={`Resend invitation to ${i.name}`}
                disabled={busy === i.id}
                onClick={() => void resend(i)}
              >
                <ForwardToInboxRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            {i.status === 'PENDING' && (
              <Tooltip title="Withdraw invitation">
                <IconButton
                  size="small"
                  aria-label={`Revoke invitation to ${i.name}`}
                  onClick={() => setRevoking(i)}
                  sx={{ '&:hover': { color: 'error.main' } }}
                >
                  <UndoRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={data?.data}
        rowKey={(i) => i.id}
        loading={isLoading}
        page={data?.page}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        toolbar={
          <>
            <TextField
              select
              label="Show"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as Filter);
                setPage(1);
              }}
              sx={{ maxWidth: { sm: 200 } }}
            >
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="ACCEPTED">Accepted</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
              <MenuItem value="REVOKED">Withdrawn</MenuItem>
              <MenuItem value="ALL">All invitations</MenuItem>
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Links stay valid for 72 hours and work once.
            </Typography>
          </>
        }
        empty={
          <EmptyState
            icon={<MarkEmailReadRounded />}
            title={filter === 'PENDING' ? 'No pending invitations' : 'Nothing here'}
            body="Invite a staff member by email, SMS or WhatsApp. They set their own password when they accept."
            action={canManage ? { label: 'Invite staff', onClick: onInvite } : undefined}
          />
        }
      />
      <ConfirmDialog
        open={Boolean(revoking)}
        title="Withdraw this invitation?"
        body={`The link sent to ${revoking?.name ?? ''} stops working. You can invite them again later.`}
        confirmLabel="Withdraw"
        danger
        onClose={() => setRevoking(null)}
        onConfirm={async () => {
          if (!revoking) return;
          try {
            await unwrap(
              api.POST('/staff/invitations/{id}/revoke', {
                params: { path: { id: revoking.id } },
              }),
            );
            await qc.invalidateQueries({ queryKey: keys.invitations });
            void qc.invalidateQueries({ queryKey: keys.subscriptionStatus });
            notify.success('Invitation withdrawn');
          } catch (e) {
            notify.error(e);
          }
        }}
      />
    </>
  );
}

export const inviteSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter their name').max(200),
    channel: z.enum(['EMAIL', 'SMS', 'WHATSAPP']),
    email: z.union([z.literal(''), z.email('Enter a valid email').max(254)]),
    phone: z.string().trim().max(40),
    roleIds: z.array(z.string()).min(1, 'Pick at least one role'),
  })
  .superRefine((v, ctx) => {
    if (v.channel === 'EMAIL' && !v.email)
      ctx.addIssue({ code: 'custom', path: ['email'], message: 'Enter the email to send to' });
    if (v.channel !== 'EMAIL' && !/^\+?[0-9 ()-]{6,}$/.test(v.phone))
      ctx.addIssue({
        code: 'custom',
        path: ['phone'],
        message: 'Enter the phone number to send to',
      });
  });
type InviteValues = z.infer<typeof inviteSchema>;

export function InviteDialog({
  onClose,
  onSent,
}: {
  onClose: () => void;
  onSent: (r: InvitationSend) => void;
}) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const qc = useQueryClient();
  const notify = useNotify();
  const {
    control,
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { name: '', channel: 'EMAIL', email: '', phone: '', roleIds: [] },
  });
  const channel = watch('channel');

  const onSubmit = handleSubmit(async (v) => {
    try {
      const r = await unwrap(
        api.POST('/staff/invitations', {
          body: {
            name: v.name.trim(),
            channel: v.channel,
            roleIds: v.roleIds,
            ...(v.email ? { email: v.email } : {}),
            ...(v.phone.trim() ? { phone: v.phone.trim() } : {}),
          },
        }),
      );
      await qc.invalidateQueries({ queryKey: keys.invitations });
      void qc.invalidateQueries({ queryKey: keys.subscriptionStatus });
      onClose();
      onSent(r);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'EMAIL_TAKEN') {
        setError('email', { message: 'Someone in your shop already uses this email' });
        return;
      }
      if (e instanceof ApiError && e.code === 'PHONE_TAKEN') {
        setError('phone', { message: 'Someone in your shop already uses this phone' });
        return;
      }
      if (e instanceof ApiError && e.code === 'INVITATION_PENDING') {
        setError(v.channel === 'EMAIL' ? 'email' : 'phone', {
          message: 'An invitation is already pending — resend it from the list instead',
        });
        return;
      }
      if (!applyFieldErrors(e, ['name', 'email', 'phone', 'roleIds', 'channel'], setError))
        notify.error(e);
    }
  });

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={phone}
    >
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Invite a staff member
          <Typography variant="body2" color="text.secondary">
            They get a single-use link to set their own password and join your shop.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Their name"
              autoFocus
              {...register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
            />
            <div>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Send by
              </Typography>
              <Controller
                control={control}
                name="channel"
                render={({ field }) => (
                  <Stack direction="row" spacing={1} role="radiogroup" aria-label="Send by">
                    {CHANNELS.map((c) => {
                      const selected = field.value === c.value;
                      const Icon = c.icon;
                      return (
                        <Button
                          key={c.value}
                          role="radio"
                          aria-checked={selected}
                          variant={selected ? 'contained' : 'outlined'}
                          startIcon={<Icon />}
                          onClick={() => field.onChange(c.value)}
                          sx={{ flex: 1 }}
                        >
                          {c.label}
                        </Button>
                      );
                    })}
                  </Stack>
                )}
              />
            </div>
            <FieldGrid>
              <TextField
                label={channel === 'EMAIL' ? 'Email' : 'Email (optional)'}
                type="email"
                {...register('email')}
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />
              <TextField
                label={channel === 'EMAIL' ? 'Phone (optional)' : 'Phone'}
                type="tel"
                placeholder="+92 300 1234567"
                {...register('phone')}
                error={Boolean(errors.phone)}
                helperText={errors.phone?.message}
              />
            </FieldGrid>
            <div>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Roles
              </Typography>
              <Controller
                control={control}
                name="roleIds"
                render={({ field }) => (
                  <RolePicker
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.roleIds?.message}
                  />
                )}
              />
            </div>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={isSubmitting}
            startIcon={<SendRounded />}
          >
            Send invitation
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** After sending: confirms delivery, or hands over the link when it could not be sent. */
export function InviteSentDialog({
  result,
  onClose,
}: {
  result: InvitationSend;
  onClose: () => void;
}) {
  const sent = result.delivery.status === 'SENT';
  const to = result.invitation.email ?? result.invitation.phone ?? result.invitation.name;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ pt: 4, textAlign: 'center' }}>
        <Box
          sx={(t) => {
            const c = (t.vars ?? t).palette[sent ? 'success' : 'primary'].main;
            return {
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '20px',
              display: 'grid',
              placeItems: 'center',
              color: c,
              background: `color-mix(in srgb, ${c} 12%, transparent)`,
              '& svg': { fontSize: 32 },
            };
          }}
        >
          {sent ? <MarkEmailReadRounded /> : <SendRounded />}
        </Box>
        <Typography variant="h4">{sent ? 'Invitation sent' : 'Share the invite link'}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>
          {sent
            ? `We sent ${result.invitation.name} a link at ${to}. It works once and expires in 72 hours.`
            : `We couldn’t send the ${result.delivery.channel.toLowerCase()} automatically. Copy the link below and send it to ${result.invitation.name} yourself.`}
        </Typography>
        {result.inviteUrl && (
          <Box sx={{ textAlign: 'left' }}>
            <CopyField label="Invite link" value={result.inviteUrl} />
            <Alert severity="info" sx={{ mt: 1.5 }}>
              This link is shown only once. If it gets lost, use “Resend” for a new one.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'center' }}>
        <Button variant="contained" onClick={onClose} sx={{ minWidth: 140 }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
